import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { BulkRevokeDocumentsDto } from '@dealstack/shared/lib/generated';

const bulkRevokeDocumentsRequest = async ({
	dealId,
	programmeId,
	body,
}: {
	dealId: string;
	programmeId: string;
	body: BulkRevokeDocumentsDto;
}) => {
	const endpoint = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_BULK_REVOKE_POST,
		{
			dealId,
			programmeId,
		}
	);
	const response = await makeRequest<ApiV1.Response<void>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
	return response;
};

export const useBulkRevokeDocuments = ({
	dealId,
	programmeId,
	onSuccess,
	onSettled,
}: {
	dealId: string;
	programmeId: string;
	onSuccess?: () => void;
	onSettled?: () => void;
}) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const handleSuccess = async () => {
		showToastV2({
			title: tMipManage('documents_page.toast.revoke.successful'),
			state: 'success',
		});

		await queryClient.invalidateQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET, {
					dealId,
					programmeId,
				}),
			],
		});

		onSuccess?.();
	};

	const handleError = () => {
		showToastV2({
			state: 'error',
			title: tMipManage('documents_page.toast.revoke.error'),
		});
	};

	return useMutation({
		mutationFn: (body: BulkRevokeDocumentsDto) =>
			bulkRevokeDocumentsRequest({ dealId, programmeId, body }),
		onSuccess: handleSuccess,
		onError: handleError,
		onSettled,
	});
};
